import {
  PiiAnonymizationService,
} from '@maximizer/ai/data-access';
import { removeSurroundingChars } from '@maximizer/shared/util';

export type DeAnonymizeLookup = (maskInner: string) => string | undefined;

/** Parses IQ Boost markup: (Maximizer:…), (CRM:…); transform or strip for persistence. */
export class AiMarkupUtils {
  private static readonly REF_PATTERN = /(?:\(([^)]+)\)|\{([^}]+)\})/g;
  // Balanced () / single char avoids catastrophic backtracking on partial streams.
  private static readonly MAXIMIZER_PATTERN = /(?:\(Maximizer:((?:\([^()]*\)|[^()])*)\)|Maximizer:\s*\{([^}]+)\})/g;
  private static readonly CRM_PATTERN = /\(CRM:\s*(?:\{([^}]+)\}|\(([^)]*)\))\)/gi;
  // Server wraps PII as <anonymized value="…"/>; the tag arrives raw or HTML-escaped.
  private static readonly ANONYMIZED_TAG_PATTERN = /(?:<|&lt;)anonymized\s+value="([^"]*)"\s*\/?(?:>|&gt;)/gi;

  static replaceMaximizerReferences(
    value: string,
    transform: (params: Record<string, string>) => string | null,
  ): string {
    if (!value) return '';

    return value.replace(this.MAXIMIZER_PATTERN, (_match: string, refs: string, singleRef: string) => {
      const parts: string[] = [];
      let refMatch: RegExpExecArray | null;
      this.REF_PATTERN.lastIndex = 0;
      let found = false;

      const content = refs || singleRef;

      while ((refMatch = this.REF_PATTERN.exec(content)) !== null) {
        found = true;
        const refContent = refMatch[1] || refMatch[2];
        const params = this.parseReferenceContent(refContent);
        const replacement = transform(params);
        if (replacement) parts.push(replacement);
      }

      if (!found) {
        const params = this.parseReferenceContent(content);
        const replacement = transform(params);
        if (replacement) parts.push(replacement);
      }

      return parts.join(', ');
    });
  }

  static removeMaximizerReferences(value: string): string {
    return this.replaceMaximizerReferences(value, () => null);
  }

  static replaceCRMObjects(
    value: string,
    transform: (crmContent: string) => string | null,
  ): string {
    if (!value) return '';

    return value.replace(this.CRM_PATTERN, (_match: string, curly: string, paren: string) => {
      const content = curly || paren;
      const replacement = transform(content);
      return replacement ?? '';
    });
  }

  static removeCRMObjects(value: string): string {
    return this.replaceCRMObjects(value, () => null);
  }

  static parseReferenceContent(content: string): Record<string, string> {
    const params: Record<string, string> = {};
    const parts = content.split(',');

    for (const part of parts) {
      const trimmed = part.trim();
      const colonIdx = trimmed.indexOf(':');
      const equalsIdx = trimmed.indexOf('=');

      let key: string;
      let value: string;

      if (colonIdx !== -1) {
        key = trimmed.slice(0, colonIdx).trim();
        value = trimmed.slice(colonIdx + 1).trim();
      } else if (equalsIdx !== -1) {
        key = trimmed.slice(0, equalsIdx).trim();
        value = trimmed.slice(equalsIdx + 1).trim();
      } else {
        continue;
      }

      value = removeSurroundingChars(value, '"', '"');
      value = removeSurroundingChars(value, "'", "'");
      value = value.replace(/\\"/g, '"').replace(/\\'/g, "'");

      params[key] = value;
    }

    return params;
  }

  /** Replaces every `<anonymized value="…"/>` tag (raw or escaped) with `transform(decodedValue)`. */
  static replaceAnonymizedTags(html: string, transform: (value: string) => string): string {
    if (!html) return '';
    if (!html.includes('anonymized value=')) return html;

    return html.replace(this.ANONYMIZED_TAG_PATTERN, (_match: string, value: string) =>
      transform(this.decodeMarkupEscaping(value)));
  }

  /**
   * Undoes one round of sanitizeMarkdownToHtml escaping.
   * `&amp;` is decoded last on purpose: it is escaped first upstream, so decoding it
   * first would over-decode. A name containing the literal text `&lt;` arrives as
   * `&amp;lt;` and must decode back to `&lt;`, not collapse into `<`.
   */
  static decodeMarkupEscaping(value: string): string {
    return value
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#0?39;/g, "'")
      .replace(/&amp;/g, '&');
  }

  static convertAnonymizedHoverSpans(htmlText: string): string {
    if (!htmlText) return '';

    // DeAnonymizerPipe PII spans use data-type + data-number (no hover class).
    const spanPattern = /<span([^>]*)>[\s\S]*?<\/span>/gi;
    const dataTypePattern = /\bdata-type=["']([^"']+)["']/i;
    const dataNumberPattern = /\bdata-number=["']([^"']*)["']/i;
    // Badges built from <anonymized value="…"/> carry the PII itself, not a mask.
    const dataValuePattern = /\bdata-anonymized-value=["']([^"']*)["']/i;

    return htmlText.replace(spanPattern, (fullMatch: string, attrs: string) => {
      const valueMatch = dataValuePattern.exec(attrs);
      if (valueMatch) return this.decodeMarkupEscaping(valueMatch[1] ?? '');

      const typeMatch = dataTypePattern.exec(attrs);
      const numberMatch = dataNumberPattern.exec(attrs);
      if (!typeMatch || !numberMatch) return fullMatch;

      const tokenType = (typeMatch[1] || '').toUpperCase();
      const tokenNumber = numberMatch[1] ?? '';
      return `<${tokenType}${tokenNumber}>`;
    });
  }

  static removeAllMarkupAndPII(text: string, service: PiiAnonymizationService): string {
    text = this.removeMaximizerReferences(text);
    text = this.removeCRMObjects(text);
    text = this.convertAnonymizedHoverSpans(text);
    // Raw summaries reach here with the server tag intact; keep the PII, drop the tag.
    text = this.replaceAnonymizedTags(text, (value) => value);
    text = service.deAnonymizeHtml(text);
    return text;
  }
}
